import type { DocFieldSchema } from "@/lib/erp/meta";
import type { CellSaveState } from "@/components/data-grid/types";

/**
 * Props for inline field editor components (Data, Int, Float, Currency, Textarea, Date).
 * Each component receives these props and handles its own edit lifecycle.
 */
export interface InlineFieldProps {
  field: DocFieldSchema;
  value: unknown;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  placeholder?: string;
  /** Called on Tab/Shift+Tab to advance to next/prev field */
  onAdvance?: (direction: "next" | "prev") => void;
}

/**
 * Per-field save state tracked by useInlineFieldSave.
 * Extends CellSaveState pattern with 'saved' status for checkmark indicator.
 */
export interface FieldSaveState {
  status: "idle" | "saving" | "saved" | "error";
  error?: string;
  lastSavedAt?: number;
  /** The value that failed to save — used for retry */
  failedValue?: unknown;
}

/** Default idle state */
export const IDLE_FIELD_STATE: FieldSaveState = { status: "idle" };

/**
 * Convert FieldSaveState to CellSaveState for compatibility with
 * LinkFieldPopover and SelectFieldPopover which expect CellSaveState.
 */
export function toCellSaveState(state: FieldSaveState): CellSaveState {
  return {
    status:
      state.status === "saved" || state.status === "idle" ? "idle" : state.status,
    error: state.error,
    conflict: false,
  };
}
